// The gather state: the client's ONE answer to "this Dredger cuts now"
// (R1120, R1121, `ui-map-interface.md` §6.2.1).
//
// The harvesting looks, the two always-on pieces of the fleet layer, the
// arcade match and the replay film all read the same tracker, so two of them
// can never disagree about a cut. The layer feeds it once a frame and every
// other reader only looks.
//
// **The client invents no wire field** (R1121). A hull cuts when its mission
// is GATHER and a deposit stands inside `GATHER_LIMITS.fieldSnapPx` of it.
// `mission` reaches the whole own faction and stops there, so a cut is drawn
// on an own hull and on a faction hull and on nothing else: an enemy contact
// carries no mission, no hold and no cargo cap.
//
// **The beats are WALL time.** One lot takes `GATHER_LIMITS.lotSec` at rate 1,
// which is the figure every card of mockup round 055 beats at. The map clock
// runs at 30x on a core world and 120x on a local server, so a beat paid in
// map seconds would smear on one world and crawl on the other.
//
// This is a leaf (`leaves.test.ts`): it imports the generated pack and
// `marks.ts`, and never an engine.
//
// **Nothing here allocates per frame** (R131). Every cut is a record of a
// fixed-capacity pool, the live ones stand at the front, and a record that
// dies is swapped to the back and reused.

import { GATHER_LIMITS } from '../gen/presentation';
import { depositLevel } from './marks';
import { clamp01 } from './recipe-kit';

/** One hull the tracker is told about, once a frame. */
export interface GatherHull {
  id: number;
  /** Where it is, in world px, and its heading in radians. */
  x: number;
  y: number;
  head: number;
  /**
   * The viewer's own player owns it, so `hold`, `cargoCap` and `shaft` are
   * real. A faction hull answers false and carries none of the three.
   */
  own: boolean;
  /** Its mission is `MISSION_GATHER`. */
  gathering: boolean;
  /** The wire's own `shaft` flag (R715). Own hulls only. */
  shaft: boolean;
  /** The hold in units and its cap. Both 0 on a hull the viewer cannot read. */
  hold: number;
  cargoCap: number;
  /** Its faction ink. */
  ink: number;
  /** True where the hull is off the glass this frame. */
  hidden: boolean;
}

/** One deposit the tracker is told about, beside the hull that works it. */
export interface GatherField {
  /** The site id, which a look that draws the mark hands back to the layer. */
  id: number;
  x: number;
  y: number;
  /** The commodity, 1 to 3, and what the field still holds. */
  resource: number;
  amount: number;
  capacity: number;
}

/**
 * ONE CUT, as every reader takes it.
 *
 * The record lives while the hull is in sight and the tail of its own cut has
 * not run out, so a look fades rather than snapping off.
 */
export interface GatherCut {
  /** The hull and the field. */
  id: number;
  siteId: number;
  x: number;
  y: number;
  head: number;
  ink: number;
  fx: number;
  fy: number;
  resource: number;
  /** The viewer's own player owns the hull, so the hold is readable. */
  own: boolean;

  // -- the state a look reads ----------------------------------------------

  /** True where the cut is an R715 shaft: a quarter of the rate, no drain. */
  shaft: boolean;
  /** 1 for a gather, `GATHER_LIMITS.shaftRate` for a shaft. */
  rate: number;
  /** 0 to 1, the envelope of the cut. */
  work: number;
  /** Seconds of cut so far in this visit. */
  tw: number;
  /** 0 to 1 inside the present lot, and how many lots this visit lifted. */
  lot: number;
  lotN: number;
  /** 0 to 1, the hold, or −1 where the viewer cannot read it. */
  fill: number;
  /** 1 falling to 0 over `fullSec` from the moment the hold filled. */
  full: number;
  /** 0 to `DEPOSIT_LEVELS`, the field's drain level. */
  level: number;
  /** 1 falling to 0 over `stepSec` after the field crossed a level line. */
  step: number;
  /** 1 falling to 0 over `bloomSec` after the field silted back up. */
  bloom: number;
  /** True on the frame the cut runs; false over the tail. */
  atRest: boolean;
  /** 0 to 1 over the tail once the cut stopped, else 0. */
  leaving: number;
  /** 1 while the hull is on the chart. */
  vis: boolean;
  /** True when the hold carries anything the viewer can read. */
  laden: boolean;

  // -- what the two always-on pieces read (R1120, R1123) -------------------

  /** The hold in units, and its cap. Both −1 on a hull the viewer cannot
   *  read. */
  holdUnits: number;
  cargoCap: number;
}

/**
 * How long a record outlives its own cut, in seconds.
 *
 * It is the out-ramp plus the full-hold beat: a look that fades over the
 * out-ramp and a look that flashes on the full hold must both finish.
 */
export const GATHER_TAIL_SEC = GATHER_LIMITS.rampOutSec + GATHER_LIMITS.fullSec;

export class GatherState {
  /** The pool. The live records stand at the front. */
  private readonly pool: GatherCut[] = [];
  private n = 0;

  /** Where a live record sits, by fleet id, so `get` is one lookup. `drop`
   *  and `clear` take a record's entry out, so every entry is a live slot. */
  private readonly slot = new Map<number, number>();

  /** Which records a frame noted, so `end` drops the rest. */
  private readonly seen = new Set<number>();

  /** The frame's own step in seconds of WALL time. */
  private dt = 0;

  /** The seconds since each record's cut last ran. */
  private readonly since: number[] = [];
  /** The drain level each record's field held last frame. */
  private readonly wasLevel: number[] = [];
  /** What each record's field held last frame, for the silt-up bloom. */
  private readonly wasAmount: number[] = [];
  /** How full each record's hold was last frame, for the full beat. */
  private readonly wasFill: number[] = [];

  constructor(private readonly cap: number = GATHER_LIMITS.maxCuts) {}

  /** How many cuts stand. A stats line and a test read it. */
  get count(): number { return this.n; }

  /** The live records. The array is the pool itself, so a reader walks
   *  `count` entries and keeps no reference. */
  cuts(): readonly GatherCut[] { return this.pool; }

  /** One cut by fleet id, or null. The fleet layer's two always-on pieces
   *  ask this per hull. */
  get(id: number): GatherCut | null {
    const i = this.slot.get(id);
    return i === undefined ? null : this.pool[i];
  }

  /** Takes every cut off the water (a season roll, a seek, a new hello). */
  clear(): void {
    this.n = 0;
    this.slot.clear();
    this.seen.clear();
  }

  /** Opens a frame. `dtMs` is WALL milliseconds. */
  begin(dtMs: number): void {
    this.dt = Math.max(0, dtMs) / 1000;
    this.seen.clear();
  }

  /**
   * One hull and the deposit it stands on, or null where none does.
   *
   * The caller hands in every fleet the viewer may read — an own hull or a
   * hull of the own faction — and the deposit inside `fieldSnapPx` of it. A
   * hull with no field, or with no GATHER mission, keeps no record and its
   * own record runs out its tail.
   */
  note(h: GatherHull, f: GatherField | null): void {
    const cutting = h.gathering && f !== null;
    let i = this.slot.get(h.id);
    if (i === undefined) {
      // A hull that is not cutting and has no record starts none.
      if (!cutting) return;
      if (this.n >= this.cap) return;
      i = this.n++;
      if (this.pool.length <= i) this.pool.push(blankCut());
      this.slot.set(h.id, i);
      this.reset(i, h, f);
    }
    this.seen.add(h.id);
    this.advance(i, h, f, cutting);
  }

  /** Closes a frame: every record nothing noted, and every record whose tail
   *  ran out, leaves the pool. */
  end(): void {
    for (let i = this.n - 1; i >= 0; i--) {
      const c = this.pool[i];
      if (this.seen.has(c.id) && this.since[i] <= GATHER_TAIL_SEC) continue;
      this.drop(i);
    }
  }

  // -- the pool --------------------------------------------------------------

  private drop(i: number): void {
    const c = this.pool[i];
    this.slot.delete(c.id);
    const last = --this.n;
    if (i !== last) {
      this.pool[i] = this.pool[last];
      this.pool[last] = c;
      this.since[i] = this.since[last];
      this.wasLevel[i] = this.wasLevel[last];
      this.wasAmount[i] = this.wasAmount[last];
      this.wasFill[i] = this.wasFill[last];
      this.slot.set(this.pool[i].id, i);
    }
  }

  private reset(i: number, h: GatherHull, f: GatherField): void {
    const c = this.pool[i];
    c.id = h.id;
    c.tw = 0;
    c.lot = 0;
    c.lotN = 0;
    c.full = 0;
    c.step = 0;
    c.bloom = 0;
    this.since[i] = 0;
    this.wasLevel[i] = depositLevel(f.amount, f.capacity);
    this.wasAmount[i] = f.amount;
    this.wasFill[i] = fillOf(h);
  }

  // -- the beats -------------------------------------------------------------

  private advance(i: number, h: GatherHull, f: GatherField | null, cutting: boolean): void {
    const c = this.pool[i];
    const L = GATHER_LIMITS;
    const dt = this.dt;

    c.x = h.x; c.y = h.y; c.head = h.head; c.ink = h.ink;
    c.vis = !h.hidden;
    c.own = h.own;
    if (f !== null) {
      c.siteId = f.id;
      c.fx = f.x; c.fy = f.y;
      c.resource = f.resource;
      // A hull that cannot read its own shaft flag reads the FIELD instead: a
      // worked-out field under a hull that still cuts is a shaft (R1121).
      c.shaft = h.own ? h.shaft : f.amount <= 0;
      const level = depositLevel(f.amount, f.capacity);
      // The field crossed a level line (R475), or it silted back up.
      if (level < this.wasLevel[i]) c.step = 1;
      if (this.wasAmount[i] <= 0 && f.amount > 0) c.bloom = 1;
      this.wasLevel[i] = level;
      this.wasAmount[i] = f.amount;
      c.level = level;
    }
    c.rate = c.shaft ? L.shaftRate : 1;

    // The hold. −1 says the viewer cannot read it, and a look that gauges the
    // hold must test that before it draws a gauge (R1121).
    const fillNow = fillOf(h);
    c.fill = fillNow;
    c.holdUnits = h.own ? h.hold : -1;
    c.cargoCap = h.own ? h.cargoCap : -1;
    c.laden = fillNow > LADEN_FLOOR;
    // The hold filled: the one beat the client can see without a new wire
    // field. A faction hull reads −1 and never raises it.
    if (fillNow >= 1 && this.wasFill[i] < 1) c.full = 1;
    this.wasFill[i] = fillNow;

    if (cutting) {
      this.since[i] = 0;
      c.tw += dt;
    } else {
      this.since[i] += dt;
    }
    const since = this.since[i];
    c.atRest = cutting;
    c.leaving = GATHER_TAIL_SEC > 0 ? clamp01(since / GATHER_TAIL_SEC) : 1;

    // The envelope: it ramps in over the in-ramp and out over the out-ramp.
    const inn = L.rampInSec > 0 ? clamp01(c.tw / L.rampInSec) : 1;
    const out = L.rampOutSec > 0 ? clamp01(1 - since / L.rampOutSec) : (cutting ? 1 : 0);
    c.work = Math.min(inn, out);

    // The lot beat. It stands still once the cut stops, so a look that reads
    // `lot` over the tail reads the phase the cut ended at.
    const lots = L.lotSec > 0 ? (c.tw * c.rate) / L.lotSec : 0;
    const wholeLots = Math.floor(lots);
    c.lotN = wholeLots;
    c.lot = lots - wholeLots;

    // The three beats fall on their own clocks.
    c.full = decay(c.full, dt, L.fullSec);
    c.step = decay(c.step, dt, L.stepSec);
    c.bloom = decay(c.bloom, dt, L.bloomSec);
  }
}

/** How much of the hold counts as laden, so a rounding hair does not. */
const LADEN_FLOOR = 0.02;

/** A beat that falls from 1 to 0 over `life` seconds. */
function decay(v: number, dt: number, life: number): number {
  if (v <= 0 || life <= 0) return 0;
  const next = v - dt / life;
  return next > 0 ? next : 0;
}

/** The hold as a share of the cap, or −1 where the viewer cannot read it. */
function fillOf(h: GatherHull): number {
  if (!h.own || h.cargoCap <= 0) return -1;
  return clamp01(h.hold / h.cargoCap);
}

function blankCut(): GatherCut {
  return {
    id: 0, siteId: 0, x: 0, y: 0, head: 0, ink: 0xffffff,
    fx: 0, fy: 0, resource: 1, own: false,
    shaft: false, rate: 1, work: 0, tw: 0,
    lot: 0, lotN: 0, fill: -1, full: 0, level: 0, step: 0, bloom: 0,
    atRest: false, leaving: 0, vis: true, laden: false,
    holdUnits: -1, cargoCap: -1,
  };
}
